import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  ButtonBase,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Icon,
  Tooltip,
} from '@mui/material';
import withStyles from '@mui/styles/withStyles';

import {
  ClaimsDateProvider, 
  TitleTooltip,
  LoadingView,
  ServiceLineSelect,
  PatientTypeSelect,
  MarketOrTagSelect,
  Paginateable,
} from 'components';

import { StoreUtil, PatientType } from 'doctivity-shared/utils';
import { ColorUtil, MarketsUtil, ServiceLineUtil, QueryParamUtil } from 'utils';
import { ClaimsPageQueryParams } from 'params';
import { loadAnalytics } from 'store/actions/analyticsActions';
import {
  listMarkets,
} from 'store/actions/marketsActions';
import { upsertSavedCriteria } from 'store/actions/savedCriteriaActions';
import axiosInstance from 'utils/axiosUtil';

const styles = (theme) => ({
  container: {
    padding: theme.spacing(3),
  },
  sortRow: {
    display: 'flex',
    flexDirection: 'row',
  },
  card: {},
  filterDropDown: {
    width: 120,
    marginRight: theme.spacing(),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  dropDown: {
    minWidth: 120,
    marginBottom: theme.spacing(2),
  },
  dropDownContainer: {
    display: 'flex',
    flexDirection: 'row',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  filterLabel: {
    backgroundColor: 'white',
    paddingRight: 4,
  },
  noData: {
    textAlign: 'center',
    paddingTop: 50,
    minHeight: 200,
  },
  colorKey: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    fontSize: 12,
    fontWeight: 400,
    minHeight: 40,
    [theme.breakpoints.down('md')]: {
      flexWrap: 'wrap',
    },
  },
  colorKeyIcon: {
    width: 14,
    height: 14,
    marginRight: 2,
    borderRadius: 2,
  },
  colorKeyText: {
    marginRight: 12,
  },
  providerRow: {
    display: 'flex',
    justifyContent: 'flex-start',
    height: 24,
    marginBottom: 4,
    color: '#2E93fA',
    fontSize: 12,
    fontWeight: 300,
    position: 'relative',
    width: '100%',
    whiteSpace: 'nowrap',
  },
  providerBar: {
    position: 'absolute',
    backgroundColor: '#2E93fA',
    transitionProperty: 'width',
    transitionDelay: '1s',
    transitionDuration: '1s',
    width: 0,
    height: 24,
    top: 0,
    left: 0,
    borderRadius: 2,
    zIndex: 1,
    overflow: 'hidden',
    color: '#ffffff',
    display: 'flex',
    justifyContent: 'flex-start',
  },
  providerName: {
    paddingTop: 2,
    paddingLeft: 8,
    zIndex: 0,
  },
  providerNameIcon: {
    fontSize: 10,
    paddingLeft: 6,
  },
  providerCount: {
    position: 'absolute',
    top: 7,
    left: 200,
    fontSize: 11,
    fontWeight: 300,
    zIndex: 0,
  },
  providerNameBar: {
    paddingTop: 6,
    paddingLeft: 8,
    zIndex: 3,
    overflow: 'hidden',
  },
  providerCountBar: {
    position: 'absolute',
    top: 7,
    left: 200,
    fontSize: 11,
    fontWeight: 300,
    zIndex: 4,
    overflow: 'hidden',
  },
  barColor: {
    zIndex: 1,
    position: 'absolute',
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
  },
  affiliatedBar: {
    backgroundColor: ColorUtil.AffiliatedColor,
  },
  unaffiliatedBar: {
    backgroundColor: ColorUtil.NonAffiliatedColor,
  },
  infoTip: {
    fontSize: 13,
    fontWeight: 200,
  },
  spacer: {
    width: theme.spacing(),
  },
  expandSpacer: {
    flexGrow: 1,
  },
});

class ClaimsSharedAffiliation extends React.Component {

  // Number of seconds to wait before saving the currently selected criteria
  saveCriteriaInterval = 3; 
  
  saveCriteriaTimer = null; 

  constructor(props) {
    super(props);

    this.state = {
      defaultParams: false, // Used when user does not have any saved criteria and going to /claims with no query string
      isMarket: true,
      market: null,
      tag: null,
      serviceline: 'all',
      before:  false,
      affiliation: 'all',
      setParams: false,
      patientType: PatientType.ALL_PATIENTS,
      lastTimeCriteriaChanged: null,
      sort: ['shared_patients', 'DESC'],
      query: {
        limit: 50,
        offset: 0,
      },
    };
  }

  componentDidMount() {
    const { dispatch, router, network } = this.props;

    if (!this.props.markets || StoreUtil.needsLoadLongCache(this.props.markets)) {
      dispatch(
        listMarkets(this.props.clientId),
      );
    }

    if (StoreUtil.needsLoadNoCache(network)) {
      this.fetchData();
    }

    if (router.query.size === 0 || (router.query.size === 1 && router.query.has('report'))) {
      this.loadFromLastSaved();
    } else {
      this.setState({
        setParams: true, // tell update to use query string
      });
    }
    this.saveCriteriaTimer = setInterval(() => {
      if (this.state.lastTimeCriteriaChanged !== null) {
        let currentTime = moment();

        let duration = moment.duration(currentTime.diff(this.state.lastTimeCriteriaChanged));
        let secondsSinceLastSave = duration.asSeconds();
        if (secondsSinceLastSave > this.saveCriteriaInterval) {
          // Build criteria_json
          // At this point we should have the most up to date criteria stored in state
          let criteriaArray = [
            {
              field: 'before',
              value: this.state.before,
            },
            {
              field: 'affiliation',
              value: this.state.affiliation,
            },
            {
              field: 'isMarket',
              value: this.state.isMarket,
            },
            {
              field: 'market',
              value: this.state.market?.id,
            },
            {
              field: 'tag',
              value: this.state.tag?.id,
            },
            {
              field: 'report',
              value: 'shared_affiliation',
            },
            {
              field: 'patient_type',
              value: this.state.patientType,
            },
            {
              field: 'serviceLine',
              value: this.state.serviceline,
            }
          ];
          dispatch(upsertSavedCriteria({
            criteria_type_id: 2,
            criteria_json: JSON.stringify({
              criteria: criteriaArray,
            }),
            client_id: this.props.clientId,
            user_id: this.props.user.id,
          }));

          this.setState({
            lastTimeCriteriaChanged: null
          });
        }
      }
    }, 1000);
  }

  componentWillUnmount() {
    clearInterval(this.saveCriteriaTimer);
  }

  marketsChanged(newMarkets, oldMarkets) {
    if (newMarkets === null || oldMarkets === null && newMarkets !== oldMarkets) {
      return true;
    }
    if (newMarkets && oldMarkets && newMarkets.data && oldMarkets.data) {
      if (newMarkets.data.length !== oldMarkets.data.length) {
        return true;
      }
      if (newMarkets.meta && newMarkets.meta.statusTime && oldMarkets.meta && oldMarkets.meta.statusTime) {
        return newMarkets.meta.statusTime.statusSuccess !== oldMarkets.meta.statusTime.statusSuccess;
      }
    }
    return false;
  }

  componentDidUpdate(prevProps) {
    const { router } = this.props;

    if (this.props.clientId !== prevProps.clientId || this.marketsChanged(this.props.markets, prevProps.markets)) {
      router.navigate('/claims?report=shared_affiliation');
      this.loadFromLastSaved();
      return;
    }

    if (this.state.setParams) {
      const queryClaimsDate = router.query.get(ClaimsPageQueryParams.CLAIMS_DATE.name);
      const queryMarket = router.query.get(ClaimsPageQueryParams.MARKET.name);
      const queryServiceLine = router.query.get(ClaimsPageQueryParams.SERVICELINE.name);
      const queryPatientType = router.query.get(ClaimsPageQueryParams.PATIENT_TYPE.name);
      const queryAffiliation = router.query.get(ClaimsPageQueryParams.AFFILIATION.name);
      const marketsLoaded = StoreUtil.isLoaded(this.props.markets);
      const queryIsMarket = router.query.get('isMarket');
      const queryTag = router.query.get('tag');

      if (marketsLoaded) {
        let querySelectedMarket = null;
        const marketsData = StoreUtil.getData(this.props.markets);
        if (marketsData && marketsData.length > 0) {
          querySelectedMarket = marketsData.find((market) => market.id === parseInt(queryMarket, 10));
        }
        if (!querySelectedMarket && marketsData.length > 0) {
          querySelectedMarket = marketsData[0];
        }

        // console.log('set state from query string');
        this.setState({
          setParams: false,
          before: String(queryClaimsDate).toLowerCase() == 'true' ?? false,
          isMarket: queryIsMarket === 'true' && !!queryTag,
          market: querySelectedMarket,
          tag: queryTag ? { id: queryTag } : null,
          serviceline: queryServiceLine,
          patientType: queryPatientType ?? PatientType.ALL_PATIENTS,
          affiliation: queryAffiliation ?? 'all'
        }, () => {
          this.fetchData();
        });
      }
    }

    if (this.state.defaultParams && (router.query.size === 0  || (router.query.size === 1 && router.query.has('report')))) {
      const marketsLoaded = StoreUtil.isLoaded(this.props.markets);
      if (marketsLoaded) {
        // console.log('set default params');
        this.setState({
          defaultParams: false,
          before: false,
          serviceline: ServiceLineUtil.getDefault(),
          isMarket: true,
          market: this.props.market,
          patientType: PatientType.ALL_PATIENTS,
          affiliation: 'all',
        }, () => {
          this.fetchData();
        });
      }
    }

  }

  loadFromLastSaved = () => {
    console.log('load last saved...');
    const { router } = this.props;
    axiosInstance.get('/UsersSavedCriteria', {
      params: {
        opts: {
          clientId: this.props.clientId,
          criteriaTypeId: 2
        }
      }
    }).then((savedCriteria) => {
      if (savedCriteria.data && savedCriteria.data.length > 0) {
        const criteria =   savedCriteria.data[0].criteria_json;
      
        let mostRecent = (typeof criteria === 'string' ? JSON.parse(criteria) : criteria)?.criteria;
    
        let mostRecentAffiliated = mostRecent.find((c) => c.field === 'affiliation');
        let mostRecentBefore = mostRecent.find((c) => c.field === 'before');
        let mostRecentServiceLine = mostRecent.find((c) => c.field === 'serviceline');
        let mostRecentMarket = mostRecent.find((c) => c.field === 'market');
        let mostRecentTag = mostRecent.find((c) => c.field === 'tag');
        let mostRecentIsMarket = mostRecent.find((c) => c.field === 'isMarket');

        console.log('set state from recent search');
        QueryParamUtil.setQueryParamsFromProps(
          { 
            report: 'shared_affiliation',
            market: mostRecentMarket?.value,
            tag: mostRecentTag?.value,
            isMarket: mostRecentIsMarket?.value ?? true,
            serviceline: mostRecentServiceLine?.value ?? 'all',
            before: mostRecentBefore?.value ?? false,
            affiliation: mostRecentAffiliated?.value,
            router 
          },
          ClaimsPageQueryParams
        );  

        this.setState({
          setParams: true
        });

      } else {
        this.setState({
          defaultParams: true
        });
      }
    });
  }

  updateQueryStringWithCriteria = () => {
    const { router } = this.props;
    console.log('oncriteria changed', this.state);

    QueryParamUtil.setQueryParamsFromProps(
      { 
        report: 'shared_affiliation',
        before: this.state.before, 
        isMarket: this.state.isMarket,
        market: this.state.market?.id,
        tag: this.state.tag?.id,
        serviceline: this.state.serviceline,
        affiliation: this.state.affiliation,
        patientType: this.state.patientType,
        router 
      },
      ClaimsPageQueryParams
    );

  }

  fetchData() {
    const { dispatch, clientId } = this.props;
    const { serviceline, before, affiliation } = this.state;

    dispatch(
      loadAnalytics({
        type: 'CLAIMS_SHARED_AFFILIATION',
        filter: {
          serviceline: serviceline === 'all' ? undefined : serviceline ?? ServiceLineUtil.getDefault(),
          before,
          client_id: clientId,
          market: this.state.isMarket ? this.state.market : undefined,
          tag: !this.state.isMarket ? this.state.tag : undefined,
          patient_type: this.state.patientType || PatientType.ALL_PATIENTS,
          affiliated:
            affiliation === 'all'
              ? undefined
              : affiliation === 'affiliated'
              ? true
              : false,
        },
        opts: {
          ...this.state.query,
          sort: this.state.sort,
        },
      })
    );
  }

  render() {
    const { classes, network } = this.props;
    const { patientType } = this.state;

    if (!network || !StoreUtil.isLoaded(network)) {
      return <LoadingView />;
    }

    let max = 15;
    network.data?.rows?.forEach((row) => {
      max = Math.max(max, row.shared_patients);
    });

    return (
      <div className={classes.container}>
        <ClaimsDateProvider />
        <Card className={classes.card}>
          <CardContent>
            <TitleTooltip title='Shared Patients by Provider Affiliation' tooltip='This chart shows the % of patients that are shared with other Affiliated Providers by the selected filters. The chart is sorted by a directional calculation of total projected patients or total projected pediatric patients.' />
            <div className={classes.dropDownContainer}>
              <FormControl
                variant='outlined'
                className={classes.filterDropDown}
                key='direction'
              >
                <InputLabel className={classes.filterLabel}>
                  Claims Date
                </InputLabel>
                <Select
                  key='select'
                  value={this.state.before}
                  onChange={this.onDirectionChange}
                  label='Patient Seen By'
                >
                  <MenuItem value={false} key='after'>
                    After
                  </MenuItem>
                  <MenuItem value key='before'>
                    Before
                  </MenuItem>
                </Select>
              </FormControl>
              <div className={classes.dropDown}>
                <MarketOrTagSelect 
                  filter={{
                    type: this.state.isMarket ? 'market' : 'tag',
                    market: this.state.market,
                    tag: this.state.tag,
                  }}
                  onChange={this.onMarketChange}
                  allowTagSelection={true}
                  markets={this.props.markets}
                  market={this.props.market}
                  type='providers'
                />
              </div>
              <div className={classes.dropDown}>
                <ServiceLineSelect
                  value={this.state.serviceline || ServiceLineUtil.getDefault()}
                  onChange={this.onServicelineChange}
                  showSubServicelines
                  showAll
                />
              </div>
              <div className={classes.dropDown}>
                <PatientTypeSelect
                  noCapturedClaims
                  noModifyAppState
                  onChange={this.onPatientTypeChange}
                  value={patientType.toUpperCase()}
                />
              </div>
              <FormControl
                variant='outlined'
                className={classes.dropDown}
                key='affiliated'
              >
                <InputLabel className={classes.filterLabel}>
                  Affiliation
                </InputLabel>
                <Select
                  key='select'
                  value={this.state.affiliation}
                  onChange={this.onAffiliationChange}
                  label='Affiliation'
                >
                  <MenuItem value='all' key='all'>
                    <em>All</em>
                  </MenuItem>
                  <MenuItem value='affiliated' key='affiliated'>
                    Affiliated
                  </MenuItem>
                  <MenuItem value='non-affiliated' key='non-affiliated'>
                    Non-Affiliated
                  </MenuItem>
                </Select>
              </FormControl>
            </div>
            <div className={classes.sortRow}>
              <FormControl
                variant='outlined'
                className={classes.dropDown}
                key='sort'
              >
                <InputLabel className={classes.filterLabel}>
                  Sort By
                </InputLabel>
                <Select
                  key='select'
                  value={this.state.sort[0]}
                  onChange={this.onSortChange}
                  label='Sort By'
                >
                  <MenuItem value='shared_patients' key='shared_patients'>
                    Total Shared Patients
                  </MenuItem>
                  <MenuItem value='shared_patients_affiliated' key='shared_patients_affiliated'>
                    Patients from Affiliated Providers
                  </MenuItem>
                </Select>
              </FormControl>
              <div className={classes.spacer} />
              <FormControl
                variant='outlined'
                className={classes.dropDown}
                key='sort-direction'
              >
                <InputLabel className={classes.filterLabel}>
                  Sort Direction
                </InputLabel>
                <Select
                  key='select'
                  value={this.state.sort[1]}
                  onChange={this.onSortDirectionChange}
                  label='Sort Direction'
                >
                  <MenuItem value='DESC' key='DESC'>
                    Highest to Lowest
                  </MenuItem>
                  <MenuItem value='ASC' key='ASC'>
                    Lowest to Highest
                  </MenuItem>
                </Select>
              </FormControl>
              <div className={classes.expandSpacer} /> 
              <Paginateable
                data={network.data}
                query={this.state.query}
                onQueryChange={this.onQueryChange}
              />
            </div>

            {network.data.rows?.length === 0 && (
              <Typography className={classes.noData}>
                No shared patients.
              </Typography>
            )}
            {network.data.rows?.length > 0 && (
              <div className={classes.colorKey}>
                <div
                  className={classes.colorKeyIcon}
                  style={{
                    backgroundColor: ColorUtil.AffiliatedColor,
                  }}
                />
                <div className={classes.colorKeyText}>Affiliated</div>

                <div
                  className={classes.colorKeyIcon}
                  style={{
                    backgroundColor: ColorUtil.NonAffiliatedColor,
                  }}
                />
                <div className={classes.colorKeyText}>Non-Affiliated</div>
              </div>
            )}
            {network.data.rows?.length > 0 &&
              network.data.rows.map((row, index) => {
                let percent = 0;
                if (row.shared_patients && row.shared_patients_affiliated) {
                  percent = row.shared_patients_affiliated / row.shared_patients * 100;
                }
                let percentLabel = `${percent.toFixed(2)}%`;
                if (index === 0) {
                  percentLabel += ' patients shared with affiliated providers';
                }

                return (
                  <ButtonBase
                    key={`provider:${row.id}`}
                    className={classes.providerRow}
                    href={`/providers/${row.id}/market`}
                  >
                    <div
                      className={classes.providerBar}
                      style={{
                        width: `${
                          (row.shared_patients
                            ? row.shared_patients / max
                            : 0) * 100
                        }%`,
                      }}
                    >
                      <div className={classes.barColor}>
                        <div className={classes.affiliatedBar} style={{ width: `${percent}%` }} />
                        <div className={classes.unaffiliatedBar} style={{ width: `${100-percent}%` }} />
                      </div>
                      <div className={classes.providerNameBar}>
                        {`${row.last_name}, ${row.first_name}`}
                        {row.is_affiliated && this.getAffiliatedIcon()}
                      </div>
                      <div className={classes.providerCountBar}>
                        <span>
                          {percentLabel}
                        </span>
                      </div>
                    </div>
                    <div className={classes.providerName}>
                      {`${row.last_name}, ${row.first_name}`}
                      {row.is_affiliated && this.getAffiliatedIcon()}
                    </div>
                    <div className={classes.providerCount}>
                      <span>
                        {percentLabel}
                      </span>
                    </div>
                  </ButtonBase>
                );
              })}
          </CardContent>
        </Card>
      </div>
    );
  }

  getAffiliatedIcon() {
    return (
      <Tooltip
        placement='bottom-start'
        title={
          <Typography className={this.props.classes.infoTip}>
            Affiliated Provider
          </Typography>
        }
      >
        <Icon className={this.props.classes.providerNameIcon}>
          hub
        </Icon>
      </Tooltip>
    );
  }

  onServicelineChange = (id) => {
    this.setState(
      {
        serviceline: id,
      },
      this.onCriteriaChanged
    );
  };

  onDirectionChange = (event) => {
    this.setState(
      {
        before: event.target.value,
      },
      this.onCriteriaChanged
    );
  };

  onAffiliationChange = (event) => {
    this.setState(
      { affiliation: event.target.value, },
      this.onCriteriaChanged
    );
  };

  onPatientTypeChange = (value) => {
    this.setState({ patientType: value, }, this.onCriteriaChanged);
  };

  onMarketChange = (filter) => {
    this.setState((state) => {
      return {
        isMarket: filter.type === 'market',
        market: filter.type === 'market' ? filter.market : state.market,
        tag: filter.type === 'tag' ? filter.tag : state.tag,
      };
    }, this.onCriteriaChanged);
  };

  onCriteriaChanged = () => {
    this.updateQueryStringWithCriteria();
    this.fetchData();

    this.setState({
      lastTimeCriteriaChanged: moment()
    });
  };

  onQueryChange = (query) => {
    this.setState(
      { query },
      this.fetchData,
    );
  };

  onSortChange = (event) => {
    this.setState(
      (state) => ({ sort: [event.target.value, state.sort[1]] }),
      this.fetchData,
    );
  };

  onSortDirectionChange = (event) => {
    this.setState(
      (state) => ({ sort: [state.sort[0], event.target.value] }),
      this.fetchData,
    );
  };

}

ClaimsSharedAffiliation.propTypes = {
  classes: PropTypes.object,
  dispatch: PropTypes.func.isRequired,
  router: PropTypes.object.isRequired,
  clientId: PropTypes.number.isRequired,
  query: PropTypes.object.isRequired,
  network: PropTypes.object,
  market: PropTypes.object,
  markets: PropTypes.object,
  user: PropTypes.object
};

function mapStateToProps(state) {
  return {
    clientId: state.app.selectedClient,
    network: StoreUtil.get(state.analytics, 'CLAIMS_SHARED_AFFILIATION'),
    market: MarketsUtil.getSelectedMarket(state),
    markets: MarketsUtil.getMarkets(state),
    user: state.user,
  };
}

const styled = withStyles(styles)(ClaimsSharedAffiliation);
const connected = connect(mapStateToProps)(styled);
export { connected as ClaimsSharedAffiliation };
